import classNames from "classnames";
import React, { FC, ReactNode, useMemo, useState } from "react";
import { LuCheck, LuLock } from "react-icons/lu";

import {
  passwordChecks,
  passwordStrength,
  strengthLabel,
} from "./journey.utils";

/**
 * The join / sign-up pages (owner 2026-10-25): purple panel on the left (on phones a band on top),
 * form on the right. Same look as the mockups the owner approved.
 */
export const JourneyLayout: FC<{ side: ReactNode; children: ReactNode }> = ({
  side,
  children,
}) => (
  <div
    className={
      "grid min-h-screen grid-cols-1 bg-white font-Manrope text-[#1F2433] lg:grid-cols-2"
    }>
    <div
      className={
        "relative flex flex-col justify-between gap-8 overflow-hidden bg-gradient-to-br from-[#5B3EF0] to-[#8F6BFF] px-6 py-8 text-white lg:p-14"
      }>
      <span
        className={
          "pointer-events-none absolute -right-36 -top-32 h-[420px] w-[420px] rounded-full bg-white/[0.08]"
        }
      />
      <span
        className={
          "pointer-events-none absolute -bottom-16 -left-20 h-[260px] w-[260px] rounded-full bg-white/[0.08]"
        }
      />
      <div className={"relative text-2xl font-extrabold"}>ProCharted</div>
      <div className={"relative"}>{side}</div>
      <div className={"relative hidden lg:block"} />
    </div>
    <div className={"flex items-center justify-center px-5 py-10 sm:px-8"}>
      <div className={"w-full max-w-[440px]"}>{children}</div>
    </div>
  </div>
);

export const JourneyTitle: FC<{
  step?: string;
  title: ReactNode;
  text?: ReactNode;
}> = ({ step, title, text }) => (
  <>
    {step && (
      <span
        className={
          "inline-flex rounded-full bg-[#F3F0FF] px-2.5 py-1 text-xs font-bold text-[#4B32D6]"
        }>
        {step}
      </span>
    )}
    <h1 className={"mb-1.5 mt-3.5 text-[28px] font-extrabold leading-tight"}>
      {title}
    </h1>
    {text && <div className={"mb-6 text-[15px] text-[#7A7F8C]"}>{text}</div>}
  </>
);

export const StepBars: FC<{ total: number; done: number }> = ({
  total,
  done,
}) => (
  <div className={"mb-4 flex gap-2"} aria-hidden={true}>
    {Array.from({ length: total }, (_, index) => (
      <span
        key={index}
        className={classNames(
          "h-[5px] flex-1 rounded",
          index < done ? "bg-[#6B4EFF]" : "bg-[#E6E8EE]",
        )}
      />
    ))}
  </div>
);

export const JOURNEY_LABEL =
  "mb-1.5 block text-[13.5px] font-bold text-[#293B5A]";
export const JOURNEY_INPUT =
  "h-11 w-full rounded-[10px] border border-[#DCE2EA] bg-white px-3.5 text-sm text-[#1F2433] outline-none focus:border-[#6B4EFF] focus:shadow-[0_0_0_3px_rgba(107,78,255,0.12)]";
export const JOURNEY_BUTTON =
  "flex h-12 w-full items-center justify-center rounded-[10px] bg-[#6B4EFF] text-[15px] font-bold text-white shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:bg-[#C3C8D4] disabled:shadow-none";
export const JOURNEY_GHOST =
  "flex h-12 items-center justify-center rounded-[10px] border border-[#DCE2EA] bg-white px-5 text-[15px] font-bold text-[#3A4256] hover:bg-[#F8F9FB]";

export const LockedEmail: FC<{ email: string }> = ({ email }) => (
  <>
    <span className={JOURNEY_LABEL}>Email</span>
    <div
      className={
        "mb-3.5 flex h-11 items-center rounded-[10px] border border-[#DCE2EA] bg-[#F8F9FB] px-3.5 text-sm text-[#5B6478]"
      }>
      <span className={"truncate"}>{email}</span>
      <LuLock
        className={"ml-auto shrink-0"}
        size={14}
        aria-label={"Can't be changed"}
      />
    </div>
  </>
);

const CHECKS: { key: keyof ReturnType<typeof passwordChecks>; text: string }[] =
  [
    { key: "upper", text: "A capital letter" },
    { key: "lower", text: "A small letter" },
    { key: "number", text: "A number" },
    { key: "symbol", text: "A symbol, like ! or #" },
  ];

/** Password + confirm, with the rules ticking off while typing and a strength bar. */
export const PasswordFields: FC<{
  password: string;
  confirm: string;
  onPassword: (value: string) => void;
  onConfirm: (value: string) => void;
  onEnter?: () => void;
}> = ({ password, confirm, onPassword, onConfirm, onEnter }) => {
  const [visible, setVisible] = useState(false);
  const checks = useMemo(() => passwordChecks(password), [password]);
  const bars = passwordStrength(password);
  const label = strengthLabel(bars);
  const matches = !!confirm && confirm === password;
  return (
    <>
      <label htmlFor={"journey-password"} className={JOURNEY_LABEL}>
        Password
      </label>
      <div className={"relative"}>
        <input
          id={"journey-password"}
          type={visible ? "text" : "password"}
          autoComplete={"new-password"}
          value={password}
          maxLength={64}
          onChange={(event) => onPassword(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && onEnter?.()}
          className={classNames(JOURNEY_INPUT, "pr-16")}
        />
        <button
          type={"button"}
          onClick={() => setVisible((v) => !v)}
          className={
            "absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-[#6B4EFF]"
          }>
          {visible ? "Hide" : "Show"}
        </button>
      </div>
      <div className={"mb-1 mt-2.5 flex gap-1.5"} aria-hidden={true}>
        {[1, 2, 3, 4].map((n) => (
          <span
            key={n}
            className={classNames(
              "h-[5px] flex-1 rounded",
              n <= bars
                ? bars >= 3
                  ? "bg-[#22A65A]"
                  : "bg-[#F0A23B]"
                : "bg-[#E6E8EE]",
            )}
          />
        ))}
      </div>
      <div
        className={classNames(
          "mb-1.5 h-4 text-[12.5px] font-bold",
          bars >= 3 ? "text-[#22A65A]" : "text-[#B7791F]",
        )}>
        {label}
      </div>
      <div className={"mb-1 text-[12.5px] text-[#7A7F8C]"}>
        At least 8 characters and three of these:
      </div>
      <ul className={"mb-1 grid grid-cols-2 gap-x-3"}>
        <Check ok={checks.length} text={"8 characters or more"} />
        {CHECKS.map((item) => (
          <Check key={item.key} ok={checks[item.key]} text={item.text} />
        ))}
      </ul>
      <label
        htmlFor={"journey-confirm"}
        className={classNames(JOURNEY_LABEL, "mt-3.5")}>
        Confirm password
      </label>
      <div className={"relative"}>
        <input
          id={"journey-confirm"}
          type={visible ? "text" : "password"}
          autoComplete={"new-password"}
          value={confirm}
          maxLength={64}
          onChange={(event) => onConfirm(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && onEnter?.()}
          className={classNames(JOURNEY_INPUT, "pr-24")}
        />
        {matches && (
          <span
            className={
              "absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1 text-[13px] text-[#22A65A]"
            }>
            <LuCheck size={14} /> Matches
          </span>
        )}
      </div>
    </>
  );
};

const Check: FC<{ ok: boolean; text: string }> = ({ ok, text }) => (
  <li
    className={classNames(
      "my-1 flex items-center gap-2 text-[13px]",
      ok ? "text-[#1F2433]" : "text-[#8A90A2]",
    )}>
    <span
      className={classNames(
        "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold",
        ok ? "bg-[#E3F6EC] text-[#1E9E5A]" : "bg-[#F1F2F5] text-[#A0A6B4]",
      )}>
      {ok ? "✓" : "•"}
    </span>
    {text}
  </li>
);

export const JourneyError: FC<{ text?: string | null }> = ({ text }) =>
  text ? (
    <div
      role={"alert"}
      className={
        "mt-3 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
      }>
      {text}
    </div>
  ) : null;

const COLORS = [
  "#6B4EFF",
  "#FFB020",
  "#22A65A",
  "#2F80ED",
  "#D64575",
  "#8F6BFF",
  "#FF6B6B",
];

/**
 * Falling confetti for a few seconds (pure CSS, no library). Nothing moves for people who ask their
 * computer for less motion.
 */
export const Confetti: FC<{ pieces?: number }> = ({ pieces = 140 }) => {
  const items = useMemo(
    () =>
      Array.from({ length: pieces }, (_, index) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.9,
        duration: 2.4 + Math.random() * 1.8,
        drift: (Math.random() - 0.5) * 160,
        spin: 360 + Math.random() * 720,
        color: COLORS[index % COLORS.length],
        round: index % 3 === 0,
      })),
    [pieces],
  );
  return (
    <div
      aria-hidden={true}
      data-testid={"confetti"}
      className={
        "proc-confetti pointer-events-none fixed inset-0 z-[1001] overflow-hidden"
      }>
      <style>{`
        @keyframes proc-confetti-fall {
          0% { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translate3d(var(--drift), 110vh, 0) rotate(var(--spin)); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) { .proc-confetti { display: none; } }
      `}</style>
      {items.map((piece, index) => (
        <i
          key={index}
          style={
            {
              position: "absolute",
              top: 0,
              left: `${piece.left}%`,
              width: piece.round ? 8 : 9,
              height: piece.round ? 8 : 14,
              borderRadius: piece.round ? "50%" : 2,
              background: piece.color,
              animation: `proc-confetti-fall ${piece.duration}s ease-in ${piece.delay}s forwards`,
              transform: "translate3d(0, -10vh, 0)",
              "--drift": `${piece.drift}px`,
              "--spin": `${piece.spin}deg`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
};
